import { Subscription } from "rxjs";
import { OnDestroy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { FaxDirection } from "src/app/enums/fax-direction";
import { ModalName } from "src/app/enums/modal-name";
import { IClientNumber } from "src/app/models/client/client-number";
import { IContact } from "src/app/models/contact/contact";
import { ICoverPage } from "src/app/models/cover-page/cover-page";
import { IFax } from "src/app/models/fax/fax";
import { ICopyFaxPayload } from "src/app/models/fax/copy-fax-payload";
import { IForwardFaxPayload } from "src/app/models/fax/forward-fax-payload";
import { ClientService } from "src/app/services/client.service";
import { CoverPageService } from "src/app/services/cover-page.service";
import { FaxService } from "src/app/services/fax.service";
import { ModalService } from "src/app/services/modal.service";
import { ToastService } from "src/app/services/toast.service";

type ResendFaxTab = "Email" | "Fax";

@Component({
  selector: "app-resend-fax-modal",
  templateUrl: "./resend-fax-modal.component.html",
  styleUrls: ["./resend-fax-modal.component.scss"],
})
export class ResendFaxModalComponent implements OnDestroy, OnInit, OnChanges {
  private _numbersSubscription?: Subscription;
  @Input() fax: IFax | null = null;
  @Input() initialTab: ResendFaxTab = "Fax";

  @Output() close = new EventEmitter();
  @Output() submit = new EventEmitter();

  isOpen = false;
  activeTab: ResendFaxTab = "Fax";
  errorMessage = "";
  isSending = false;

  clientNumbers: IClientNumber[] = [];
  coverPages: ICoverPage[] = [];

  private _activeAddressField: "email" | "toNumber" = "toNumber";

  form = new UntypedFormGroup({
    email: new UntypedFormControl(""),
    toNumber: new UntypedFormControl(""),
    fromNumber: new UntypedFormControl(""),
    coverPage: new UntypedFormControl(""),
    sentFrom: new UntypedFormControl(""),
    deliveryTo: new UntypedFormControl(""),
    subject: new UntypedFormControl(""),
    notes: new UntypedFormControl(""),
  });

  constructor(
    private readonly _clientService: ClientService,
    private readonly _coverPageService: CoverPageService,
    private readonly _faxService: FaxService,
    private readonly _modalService: ModalService,
    private readonly _toastService: ToastService
  ) {}

  get clientNumberOptions() {
    return this.clientNumbers.map(clientNumber => clientNumber.number);
  }

  get coverPageOptions() {
    return this.coverPages.map(coverPage => coverPage.title);
  }

  get generateEmailBody(): boolean {
    return ["notes", "subject", "sentFrom", "deliveryTo"].some(
      field => !!String(this.form.get(field)?.value ?? "").trim()
    );
  }

  get isSubmitDisabled() {
    if (this.isSending) {
      return true;
    }

    const requiredFields = this.activeTab === "Email"
      ? (this.generateEmailBody ? ["email", "notes", "subject", "sentFrom", "deliveryTo"] : ["email"])
      : ["toNumber", "fromNumber", "sentFrom", "deliveryTo", "subject"];

    return requiredFields.some(field => !String(this.form.get(field)?.value ?? "").trim());
  }

  selectTab(tab: ResendFaxTab) {
    this.activeTab = tab;
    this.errorMessage = "";
  }

  changeFromNumber(number: string) {
    this.form.get("fromNumber")?.setValue(number);
  }

  changeCoverPage(coverPageTitle: string) {
    this.form.get("coverPage")?.setValue(coverPageTitle);
    const coverPage = this.coverPages.find(page => page.title === coverPageTitle);
    if (coverPage) {
      this.form.patchValue({ sentFrom: coverPage.sentFrom, fromNumber: coverPage.fromNumber });
    }
  }

  openSelectContactModal(field: "email" | "toNumber") {
    this._activeAddressField = field;
    this._modalService.openModal(ModalName.SelectContact);
  }

  selectContact(contact: IContact) {
    const value = this._activeAddressField === "email" ? contact.email : contact.faxNumber;

    this.form.get(this._activeAddressField)?.setValue(value);
  }

  clearField(field: "email" | "toNumber") {
    this.form.get(field)?.setValue("");
  }

  onClose() {
    this.isOpen = false;
    this.close.emit();
  }

  onSubmit() {
    this.errorMessage = "";

    if (this.isSubmitDisabled) {
      return;
    }

    if (this.activeTab === "Fax") {
      this.sendByFax();
    } else {
      this.sendByEmail();
    }
  }

  ngOnInit() {
    this._numbersSubscription = this._clientService.watchNumbers().subscribe(clientNumbers => {
      this.clientNumbers = clientNumbers;
    });

    this._coverPageService.getAll({ pageNumber: "1", pageSize: "10" }).subscribe(coverPages => {
      this.coverPages = coverPages.records;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.fax && this.fax !== null) {
      this.isOpen = true;
      this.resetForm(this.fax);
    }
  }

  private sendByFax() {
    if (this.fax === null) {
      return;
    }

    const formValue = this.form.value;
    let toNumber = String(formValue.toNumber ?? "").replace(/\s/g, "");
    if (/^\d{10}$/.test(toNumber)) {
      toNumber = `+1${toNumber}`;
    } else if (/^1\d{10}$/.test(toNumber)) {
      toNumber = `+${toNumber}`;
    }

    if (!/^\+[1-9]\d{10,14}$/.test(toNumber)) {
      this.errorMessage = "Enter a 10-digit fax number or a number with country code (e.g. +1 555 123 45 67)";
      return;
    }

    const payload: IForwardFaxPayload = {
      toNumber,
      fromNumber: formValue.fromNumber ?? "",
      folderName: "",
      emails: [],
    };

    this.isSending = true;
    this._faxService.forward(this.fax.id, payload).subscribe({
      next: () => {
        this.isSending = false;
        this.isOpen = false;
        this.submit.emit();
        this._toastService.callToast("Fax sent successfully");
      },
      error: error => {
        this.isSending = false;
        this.errorMessage = error?.message ?? "Failed to send fax";
      },
    });
  }

  private sendByEmail() {
    if (this.fax === null) {
      return;
    }

    const formValue = this.form.value;

    const payload: ICopyFaxPayload = {
      toEmail: formValue.email ?? "",
      generateEmailBody: this.generateEmailBody,
      deliveryTo: formValue.deliveryTo ?? "",
      sentFrom: formValue.sentFrom ?? "",
      subject: formValue.subject ?? "",
      notes: formValue.notes ?? "",
    };

    this.isSending = true;
    this._faxService.copy(this.fax.id, payload).subscribe({
      next: () => {
        this.isSending = false;
        this.isOpen = false;
        this.submit.emit();
        this._toastService.callToast("Email sent successfully");
      },
      error: error => {
        this.isSending = false;
        this.errorMessage = error?.message ?? "Failed to send email";
      },
    });
  }

  private resetForm(fax: IFax) {
    this.activeTab = this.initialTab;
    this.errorMessage = "";

    const isInbound = fax.direction === FaxDirection.Inbound;

    this.form.reset({
      email: "",
      toNumber: isInbound ? "" : fax.toNumber ?? "",
      fromNumber: (isInbound ? fax.toNumber : fax.fromNumber) ?? "",
      coverPage: "",
      sentFrom: fax.fromName ?? "",
      deliveryTo: fax.toName ?? "",
      subject: fax.subject ?? "",
      notes: fax.notes ?? "",
    });
  }

  ngOnDestroy() {
    this._numbersSubscription?.unsubscribe();
  }
}
